/* ============================================================================
   SL PROOF REVIEW — the customer's proof page

   Renders one proof: the artwork the customer uploaded, with the REAL MOTO
   production cut line drawn over it, and the two decisions they can make.

   ── THE CUT LINE IS NOT DECORATION ───────────────────────────────────────
   `cutPath` comes from the production SVG MOTO wrote, in that file's own
   coordinate frame, and the service has already checked its hash against the
   revision the customer is deciding on. This module draws THAT path and never
   computes geometry of its own -- a second cut-line generator for the customer
   view would eventually disagree with the one that drives the cutter, and the
   customer would approve a line that is not the line.

   So when geometry is missing or mismatched, this page says so and refuses to
   offer approval. A proof page that quietly drew no cut line would be a proof
   of nothing.

   ── APPROVAL IS DELIBERATE ───────────────────────────────────────────────
   One in-flight request at a time, the button disables itself on the first
   click, and the version the page was rendered from travels with the decision
   so a stale tab is refused by the server rather than silently applied.

   ── STRUCTURE ────────────────────────────────────────────────────────────
   Everything that can be a pure function is one, so the parts that decide what
   the customer sees are testable in Node without a DOM:

       proofState()   which of the five screens to show
       overlaySvg()   the cut-line overlay markup
       noteError()    whether a change request may be submitted
       isManual()     a proof image our team prepared, not artwork + cut line
       messageHtml()  the "Message from Stickr Lab" card for this version
       threadItems()  the conversation, one version at a time, oldest first
       threadHtml()   that conversation as markup, every value escaped
       afterDecision() what the page shows once a decision went through

   ── A MANUAL PROOF HAS NO CUT LINE TO DRAW ───────────────────────────────
   When staff upload a proof image, the service says `proofImage: true` and
   sends `cutLine: null`, `artworkAligned: false`. The image IS the proof, so
   it is shown from the same artwork route and nothing is drawn over it --
   an overlay fitted to a picture it was not measured against is exactly the
   misregistration the auto path refuses.
   ============================================================================ */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.SLProofReview = factory(); }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var MAX_NOTE = 500;

  /* What this page can render, sent with every proof request (see createClient). */
  var CAPS = 'caps=manual';

  /* The five screens. Named for what the CUSTOMER is doing, not for the
     record's internal state. */
  var VIEW = {
    LOADING:   'loading',
    PREPARING: 'preparing',
    REVIEW:    'review',
    REQUESTED: 'requested',
    APPROVED:  'approved',
    UNAVAILABLE: 'unavailable',
    SIGNED_OUT: 'signed_out'
  };

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /**
   * Which screen does this payload mean?
   *
   * Geometry problems outrank the proof state on purpose: a `ready` revision
   * whose cut line cannot be shown is NOT reviewable, and offering Approve on
   * it would be asking the customer to authorise something invisible.
   */
  function proofState(payload) {
    if (!payload) return VIEW.LOADING;
    if (payload.signedOut) return VIEW.SIGNED_OUT;

    var s = payload.proofState;
    if (s === 'approved') return VIEW.APPROVED;
    if (s === 'changes_requested') return VIEW.REQUESTED;
    if (s === 'preparing' || !payload.revision) return VIEW.PREPARING;

    if (payload.geometryUnavailable || payload.geometryMismatch) return VIEW.UNAVAILABLE;
    return VIEW.REVIEW;
  }

  /**
   * The cut-line overlay.
   *
   * Drawn in the production file's OWN viewBox so the path needs no
   * transformation -- reprojecting it here is exactly the kind of arithmetic
   * that drifts from the cutter. `preserveAspectRatio` matches the artwork's
   * `object-fit: contain`, so the two layers stay registered at every size.
   *
   * The line is drawn TWICE: a white casing under a red stroke, so it stays
   * visible over dark artwork and over light artwork alike. `vector-effect`
   * keeps it one visual width however far the customer zooms.
   */
  function overlaySvg(cutLine) {
    if (!cutLine || !cutLine.cutPath || !cutLine.viewBox) return '';
    var w = Number(cutLine.viewBox.w), h = Number(cutLine.viewBox.h);
    if (!(w > 0) || !(h > 0)) return '';
    var d = esc(cutLine.cutPath);
    return '<svg class="slp-cut" viewBox="0 0 ' + w + ' ' + h + '" ' +
           'preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">' +
           '<path class="slp-cut__casing" d="' + d + '" />' +
           '<path class="slp-cut__line" d="' + d + '" />' +
           '</svg>';
  }

  /**
   * Why this note cannot be submitted, or null.
   * A change request with no note is the one outcome neither the customer nor
   * the shop can act on, so it is refused before the request is made.
   */
  function noteError(note, required) {
    var s = typeof note === 'string' ? note.trim() : '';
    if (required && !s) return 'Tell us what you would like changed.';
    if (s.length > MAX_NOTE) return 'Please keep your note under ' + MAX_NOTE + ' characters.';
    return null;
  }

  /** Order facts worth showing. Only what the payload actually carries. */
  function specLines(payload) {
    var s = (payload && payload.specs) || {};
    var out = [];
    ['size', 'material', 'finish', 'border', 'quantity'].forEach(function (k) {
      if (s[k] !== undefined && s[k] !== null && s[k] !== '') {
        out.push({ key: k, value: String(s[k]) });
      }
    });
    return out;
  }

  /* ---- manual proofs and the conversation -------------------------------- */

  /** A proof image prepared by our team: the service's image flag, or a
      revision of kind 'manual'. Either way no cut line is drawn or named. */
  function isManual(payload) {
    return !!(payload && (payload.proofImage === true ||
      (payload.revision && payload.revision.kind === 'manual')));
  }

  function note(s) {
    return typeof s === 'string' && s.trim() ? s.trim() : null;
  }

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** "Oct 28, 9:14 AM" in the customer's own time zone, or '' for no time. */
  function when(ms) {
    var n = Number(ms);
    if (!(n > 0)) return '';
    var d = new Date(n);
    if (isNaN(d.getTime())) return '';
    var hr = d.getHours(), min = d.getMinutes();
    return MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + (hr % 12 || 12) + ':' +
           (min < 10 ? '0' : '') + min + ' ' + (hr < 12 ? 'AM' : 'PM');
  }

  /** The "Message from Stickr Lab" card, or '' when this version has none. */
  function messageHtml(revision) {
    var m = note(revision && revision.staffNote);
    if (!m) return '';
    return '<div class="slp-card slp-panel slp-msg" role="note">' +
             '<h2>Message from Stickr Lab</h2>' +
             '<p>' + esc(m) + '</p>' +
           '</div>';
  }

  /**
   * The conversation as a flat list, oldest first: each version's "sent"
   * entry, followed by what the customer did with it.
   *
   * A superseded version that carries a customer note is a revision request
   * the next version answered; one without is a version our team replaced on
   * its own, and it says only that it was replaced.
   */
  function threadItems(thread) {
    var list = (Array.isArray(thread) ? thread : []).filter(function (t) {
      return t && Number(t.version) > 0;
    }).slice().sort(function (a, b) { return Number(a.version) - Number(b.version); });

    var out = [];
    list.forEach(function (t, i) {
      var v = Number(t.version), next = list[i + 1];
      var tag = null, tone = null;
      if (t.state === 'superseded') { tag = 'Replaced by V' + (next ? Number(next.version) : v + 1); tone = 'old'; }
      else if (t.state === 'ready') { tag = 'Awaiting your review'; tone = 'now'; }

      out.push({ type: 'sent', version: v, title: 'PROOF V' + v + ' — SENT',
                 atMs: t.sentAtMs || null, from: 'STICKR LAB', note: note(t.staffNote),
                 tag: tag, tagTone: tone });

      var asked = note(t.customerNote);
      if (t.state === 'approved') {
        out.push({ type: 'approved', version: v, title: 'CUSTOMER — APPROVED ✓',
                   atMs: t.decidedAtMs || null, from: 'YOU', note: asked });
      } else if (t.state === 'changes_requested' || (t.state === 'superseded' && asked)) {
        out.push({ type: 'requested', version: v, title: 'CUSTOMER — REVISION REQUESTED',
                   atMs: t.decidedAtMs || null, from: 'YOU', note: asked });
      }
    });
    return out;
  }

  /** The conversation as markup, or '' when there is nothing to show yet. */
  function threadHtml(thread) {
    var items = threadItems(thread);
    if (!items.length) return '';
    return '<section class="slp-card slp-thread" aria-label="Proof history">' +
      '<h2>Proof history</h2><ol class="slp-tl">' +
      items.map(function (it) {
        var t = when(it.atMs);
        return '<li class="slp-tl__item slp-tl__item--' + it.type + '">' +
          '<div class="slp-tl__head"><b>' + esc(it.title) + '</b>' +
            (t ? '<time datetime="' + esc(new Date(Number(it.atMs)).toISOString()) + '">' + esc(t) + '</time>' : '') +
          '</div>' +
          (it.note ? '<p class="slp-tl__msg"><span>' + esc(it.from) + ':</span> ' + esc(it.note) + '</p>' : '') +
          (it.tag ? '<span class="slp-tl__tag slp-tl__tag--' + it.tagTone + '">' + esc(it.tag) + '</span>' : '') +
        '</li>';
      }).join('') +
      '</ol></section>';
  }

  /**
   * The payload to show once a decision went through.
   *
   * The decide call answers with the decision, not the whole proof. Rather
   * than reload (and briefly show the review screen again), the version the
   * customer just decided is marked in the thread the page already has. If
   * the server did send a thread, that one wins.
   */
  function afterDecision(result, current, decision, noteText, version, nowMs) {
    if (result && Array.isArray(result.thread)) return result;
    var state = decision === 'approve' ? 'approved' : 'changes_requested';
    var base = current || {}, out = {}, k;
    for (k in base) if (Object.prototype.hasOwnProperty.call(base, k)) out[k] = base[k];
    out.proofState = state;
    out.revision = {};
    for (k in (base.revision || {})) out.revision[k] = base.revision[k];
    out.revision.state = state;
    out.revision.note = note(noteText);
    out.thread = (Array.isArray(base.thread) ? base.thread : []).map(function (t) {
      if (!t || Number(t.version) !== Number(version)) return t;
      var c = {};
      for (var j in t) c[j] = t[j];
      c.state = state;
      c.decidedAtMs = nowMs;
      c.customerNote = note(noteText);
      return c;
    });
    return out;
  }

  /* ---- the client ------------------------------------------------------- */

  /**
   * @param {Object} cfg
   *   endpoint  {string}  the App Proxy base, e.g. "/apps/proof"
   *   orderId, designId
   *   fetchImpl {Function}
   */
  function createClient(cfg) {
    var c = cfg || {};
    var base = String(c.endpoint || '/apps/proof').replace(/\/+$/, '');
    var doFetch = c.fetchImpl ||
      (typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : null);
    var inFlight = false;

    /* ── caps=manual ON EVERY REQUEST ──────────────────────────────────────
       This page knows how to show a manual proof (the image, no cut line).
       The service serves a manual proof -- and lets it be decided -- only to
       a page that says so; to any other caller it answers as if the cut line
       were unavailable. Without that, an older page would draw the checkout
       preview as "the proof" and offer Approve on it. A plain query
       parameter: the App Proxy signs the whole query string. */
    function url(suffix) {
      return base + '/api/proof/' + encodeURIComponent(c.orderId) +
             '/' + encodeURIComponent(c.designId) + (suffix || '') + '?' + CAPS;
    }

    /* A 401 is not an error page. The customer simply is not signed in, and
       the caller sends them to Shopify's login with a return_url back to this
       exact proof -- which is the whole "return them to the proof they were
       trying to review" requirement. */
    function handle(res) {
      if (res.status === 401) return { signedOut: true };
      return res.json().then(function (j) {
        if (!j || !j.ok) {
          return { error: (j && j.code) || 'error',
                   message: (j && j.error) || 'That could not be loaded.',
                   currentVersion: j && j.currentVersion };
        }
        return j.value;
      });
    }

    function load() {
      if (!doFetch) return Promise.resolve({ error: 'unavailable' });
      return doFetch(url(''), { credentials: 'same-origin', cache: 'no-store' })
        .then(handle)
        .catch(function () { return { error: 'unavailable', message: 'We could not load your proof.' }; });
    }

    /**
     * Send a decision. Refuses to run twice at once -- the second click of a
     * double click is dropped here as well as being idempotent on the server.
     */
    function decide(decision, note, version) {
      if (!doFetch) return Promise.resolve({ error: 'unavailable' });
      if (inFlight) return Promise.resolve({ error: 'in_flight' });
      inFlight = true;
      return doFetch(url(''), {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: decision, note: note || null, version: version })
      })
        .then(handle)
        .catch(function () { return { error: 'unavailable', message: 'That did not go through. Please try again.' }; })
        .then(function (r) { inFlight = false; return r; });
    }

    return {
      load: load,
      decide: decide,
      /* THE PAGE BUILDS THIS, NOT THE SERVER. The payload says only WHETHER
         an aligned raster exists; the address is composed here from the same
         App Proxy endpoint every other call uses. So no storage location is
         ever serialised to the browser, and there is no URL in the payload
         for anything to tamper with. */
      artworkUrl: function () { return url('/artwork'); },
      approve: function (note, version) { return decide('approve', note, version); },
      requestChanges: function (note, version) { return decide('request_changes', note, version); },
      busy: function () { return inFlight; }
    };
  }

  /**
   * Shopify's login, returning to this exact proof.
   * The return path is same-origin by construction -- it is built here from the
   * ids, never taken from a query parameter, so this cannot become an open
   * redirect.
   */
  function loginUrl(orderId, designId, pathname) {
    var back = pathname || ('/pages/proof?order=' + encodeURIComponent(orderId) +
                            '&design=' + encodeURIComponent(designId));
    /* /customer_authentication/login + return_to, NOT /account/login +
       return_url: the store runs Shopify's new customer accounts, where the
       latter lands on Shopify's hosted account page and never comes back. */
    return '/customer_authentication/login?return_to=' + encodeURIComponent(back);
  }

  return {
    VIEW: VIEW,
    MAX_NOTE: MAX_NOTE,
    CAPS: CAPS,
    proofState: proofState,
    overlaySvg: overlaySvg,
    noteError: noteError,
    specLines: specLines,
    isManual: isManual,
    when: when,
    messageHtml: messageHtml,
    threadItems: threadItems,
    threadHtml: threadHtml,
    afterDecision: afterDecision,
    createClient: createClient,
    loginUrl: loginUrl,
    esc: esc
  };
});
